/**
 * GlobalShortcuts
 *
 * Root-level coordinator that:
 *   - mounts the command palette and the shortcuts-help modal
 *   - owns all global keybindings (Cmd+K, ?, Cmd+N, Cmd+M, Cmd+B, Cmd+1..7)
 *   - only activates when the user is authenticated (so it is a no-op on
 *     public routes like /auth, /landing, /find-trainer)
 *
 * Rendered inside BrowserRouter and AuthProvider in App.tsx.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { supabase } from "@/integrations/supabase/client";

const NAV_MAP: Record<string, string> = {
  "mod+1": "/dashboard",
  "mod+2": "/calendar",
  "mod+3": "/clients",
  "mod+4": "/messages",
  "mod+5": "/earnings",
  "mod+6": "/insights",
  "mod+7": "/settings",
};

export function GlobalShortcuts() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const enabled = !!session;

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
    navigate("/auth");
  };

  // Build navigation bindings once per render — the hook re-reads handlers
  // from a ref each keydown so identity churn is not a concern.
  const navBindings = Object.fromEntries(
    Object.entries(NAV_MAP).map(([combo, path]) => [
      combo,
      (e: KeyboardEvent) => {
        e.preventDefault();
        navigate(path);
      },
    ])
  );

  useKeyboardShortcuts(
    {
      "mod+k": (e) => {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      },
      "?": (e) => {
        // Don't hijack "?" while typing — the hook already guards this
        // for non-mod combos, but be explicit.
        e.preventDefault();
        setHelpOpen((v) => !v);
      },
      "mod+n": (e) => {
        e.preventDefault();
        navigate("/clients?add=1");
      },
      "mod+m": (e) => {
        e.preventDefault();
        navigate("/messages");
      },
      ...navBindings,
    },
    { enabled }
  );

  if (!enabled) return null;

  return (
    <>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onLogout={handleSignOut}
      />
      <KeyboardShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}

export default GlobalShortcuts;
